import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DocumentAssessmentResultsByParentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetDocumentAssessmentResultsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC document assessment result data to match the GraphQL query structure
 */
const mapTrpcDocumentAssessmentResultsToGraphQL = (
  trpcData: DocumentAssessmentResultsByParentIdResponseRow[]
): GetDocumentAssessmentResultsByParentIdQuery => {
  return {
    document_assessment_result: trpcData,
  };
};

export const useGetDocumentAssessmentResultsByParentId = (
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.documentAssessmentResultsByParentId.queryOptions(
      {
        parentId,
      }
    ),

    enabled: trpcEnabled && !skip && !!parentId,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcDocumentAssessmentResultsToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () =>
      mapTRPCRefetch(trpcRefetch, mapTrpcDocumentAssessmentResultsToGraphQL),
    error: trpcError,
  };
};
